import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Hand, Leaf, ShieldCheck, Package, Pause, Play } from "lucide-react";
import { useShop } from "@/store";
import { M, P, J } from "@/data/catalogue";
import { Reveal, MaskedLines, CountUp, EASE } from "@/components/motion";
import ProductCard from "@/components/ProductCard";

const VALUE_ICONS = [
  <Hand key="h" strokeWidth={1.2} />,
  <Leaf key="l" strokeWidth={1.2} />,
  <ShieldCheck key="s" strokeWidth={1.2} />,
  <Package key="b" strokeWidth={1.2} />,
];

function Hero() {
  const { t } = useShop();
  const mediaRef = useRef(null);
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const { scrollYProgress } = useScroll({ target: mediaRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 4, ry: px * 5 });
  };
  const toggleVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPaused(false); } else { v.pause(); setPaused(true); }
  };

  return (
    <section className="hero">
      <div className="copy">
        <motion.div className="eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: EASE }}>
          {t("hero.eb")}
        </motion.div>
        <MaskedLines lines={t("hero.h")} testid="hero-headline" />
        <motion.p className="lede" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65, ease: EASE }}>
          {t("hero.p")}
        </motion.p>
        <motion.div className="cta" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease: EASE }}>
          <Link className="btn light" to="/shop" data-testid="hero-cta-shop">{t("hero.b1")}</Link>
          <Link className="btn ghost" style={{ color: "#fff" }} to="/artisans" data-testid="hero-cta-artisans">{t("hero.b2")}</Link>
        </motion.div>
      </div>
      <div className="media" ref={mediaRef} onMouseMove={onMove} onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}>
        <motion.div
          className="frame3d"
          animate={{ rotateX: tilt.rx, rotateY: tilt.ry }}
          transition={{ type: "spring", stiffness: 60, damping: 18 }}
        >
          <motion.div style={{ y, width: "100%", height: "100%" }} initial={{ scale: 1.12, opacity: 0.4 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, ease: EASE }}>
            <video ref={videoRef} autoPlay muted loop playsInline poster={M.hero} data-testid="hero-video">
              <source src={M.hero_mp4} type="video/mp4" />
            </video>
          </motion.div>
        </motion.div>
        <button className="vtoggle" data-testid="hero-video-toggle" aria-pressed={paused} onClick={toggleVideo}>
          {paused ? <Play size={11} /> : <Pause size={11} />} {paused ? t("hero.play") : t("hero.pause")}
        </button>
      </div>
      <div className="scrollhint" aria-hidden="true"><i />{t("hero.scroll")}</div>
    </section>
  );
}

export default function Home() {
  const { t, L } = useShop();
  const vals = t("vals");
  const steps = t("proc.steps");
  const procImgs = ["yarn", "hands", "strap", "tassel"];
  const tiles = t("impact.tiles");

  return (
    <>
      <Hero />

      <div className="wrap">
        <div className="values" data-testid="values-strip">
          {vals.map((v, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{ padding: 0 }}>
                {VALUE_ICONS[i]}
                <b>{v[0]}</b>
                <p>{v[1]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <section className="sec">
        <div className="wrap">
          <Reveal className="center">
            <div className="eyebrow">{t("coll.eb")}</div>
            <h2 style={{ margin: "12px 0 10px" }}>{t("coll.h")}</h2>
            <p className="lede" style={{ margin: "0 auto" }}>{t("coll.p")}</p>
            <div className="rule" />
          </Reveal>
          <div className="grid g4" style={{ marginTop: 40 }}>
            {P.slice(0, 4).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.09}><ProductCard p={p} /></Reveal>
            ))}
          </div>
          <Reveal className="center" delay={0.1}>
            <div style={{ marginTop: 40 }}>
              <Link className="btn" to="/shop" data-testid="collection-view-all">{t("coll.all")}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec alt tight">
        <div className="wrap split">
          <Reveal><div className="im"><img src={M.cactus} alt="Cardón cacti in the La Guajira desert" loading="lazy" /></div></Reveal>
          <Reveal delay={0.12}>
            <div className="eyebrow">{t("story.eb")}</div>
            <h2>{t("story.h")}</h2>
            <p className="lede">{t("story.p")}</p>
            <div style={{ marginTop: 26 }}>
              <Link className="btn" to="/story" data-testid="story-read-more">{t("story.b")}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec">
        <div className="wrap">
          <Reveal className="center">
            <div className="eyebrow">{t("proc.eb")}</div>
            <h2 style={{ margin: "12px 0 0" }}>{t("proc.h")}</h2>
            <div className="rule" />
          </Reveal>
          <div className="process" style={{ marginTop: 40 }}>
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div>
                  <div className="im"><img src={M[procImgs[i]]} alt={s[0]} loading="lazy" /></div>
                  <b><i>{i + 1}</i>{s[0]}</b>
                  <p>{s[1]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec dark tight">
        <div className="wrap split">
          <Reveal>
            <div className="eyebrow">{t("film.eb")}</div>
            <h2>{t("film.h")}</h2>
            <p className="lede">{t("art.craftedT")}</p>
            <div style={{ marginTop: 26 }}>
              <Link className="btn light" to="/artisans" data-testid="film-meet-weavers">{t("hero.b2")}</Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="vid">
              <video autoPlay muted loop playsInline poster={M.artisan1} data-testid="artisan-film">
                <source src={M.artisan_mp4} type="video/mp4" />
              </video>
              <span className="cap">{t("film.cap")}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec alt center">
        <div className="wrap">
          <Reveal><span className="orn" /></Reveal>
          <Reveal delay={0.08}><p className="quote" style={{ marginTop: 22 }}>{t("qbanner.q")}</p></Reveal>
          <div className="stats" data-testid="impact-stats">
            {tiles.map((x, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="stat">
                  <CountUp to={x[0]} suffix={x[1]} />
                  <span>{x[2]}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div style={{ marginTop: 34 }}>
              <Link className="btn ghost" to="/impact" data-testid="impact-link">{t("nav.impact")} →</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec">
        <div className="wrap">
          <div className="split rev">
            <Reveal>
              <div className="eyebrow">{t("journal.eb")}</div>
              <h2>{t("journal.h")}</h2>
              <div style={{ marginTop: 22, display: "grid", gap: 14 }}>
                {J.slice(0, 3).map((j) => (
                  <Link key={j.id} to={`/journal/${j.id}`} data-testid={`journal-teaser-${j.id}`} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, alignItems: "baseline", borderBottom: "1px solid var(--line)", paddingBottom: 12 }}>
                    <span className="eyebrow mute">{L(j).c}</span>
                    <span style={{ fontFamily: "var(--display)", fontSize: 22 }}>{L(j).t}</span>
                  </Link>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="im tall"><img src={M.lifestyle} alt="Woman carrying a Wayuu bag through the desert" loading="lazy" /></div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
